import { useState, useMemo } from 'react';
import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Typography
} from '@mui/material';
import { NormalizedOrder, OrdersResponse } from '../../types/unwinding.types';
import { PositionsGroupedByPair } from '../../types';
import { normalizeOrdersPayload } from '../../utils/normalizeOrders';

interface OrdersPanelProps {
  data: OrdersResponse | unknown | null | undefined;
  positions?: PositionsGroupedByPair[];
  isLoading?: boolean;
}

const timeAgo = (dateStr: string): string => {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 0) return 'now';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
};

const fmtPrice = (v: number | null | undefined): string => {
  if (v == null || isNaN(v)) return '\u2014';
  if (v === 0) return '0.00';
  if (v >= 0.1) return v.toFixed(2);
  if (v >= 0.01) return v.toFixed(3);
  return v.toFixed(4);
};

const hSx = {
  fontSize: '0.56rem', fontWeight: 600, color: 'grey.500',
  py: 0.15, px: 0.15, textTransform: 'uppercase' as const,
  letterSpacing: '0.02em', whiteSpace: 'nowrap' as const,
  borderBottom: '1px solid rgba(255,255,255,0.06)',
};

const cSx = { fontSize: '0.7rem', py: 0.1, px: 0.1 } as const;

const statusColor: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  resting: 'default', live: 'info', partial: 'warning',
  executed: 'success', matched: 'success', canceled: 'error', cancelled: 'error',
};

function buildMarketLookup(positions?: PositionsGroupedByPair[]): Map<string, { name: string; url?: string }> {
  const map = new Map<string, { name: string; url?: string }>();
  if (!positions) return map;
  for (const group of positions) {
    for (const pos of group.positions) {
      const info = {
        name: pos.platform === 'kalshi'
          ? (group.kalshiTitle || group.pairName)
          : (group.polymarketTitle || group.pairName),
        url: pos.marketUrl,
      };
      if (pos.marketId) map.set(pos.marketId, info);
      if (pos.externalId && pos.externalId !== pos.marketId) map.set(pos.externalId, info);
    }
  }
  return map;
}

export const OrdersPanel = ({ data, positions, isLoading }: OrdersPanelProps) => {
  const marketLookup = useMemo(() => buildMarketLookup(positions), [positions]);
  const normalizedData = useMemo(() => normalizeOrdersPayload(data), [data]);
  const orders = normalizedData.orders;

  const summary = useMemo(() => {
    const safeSummary = normalizedData.summary;
    if (safeSummary?.kalshi && safeSummary?.polymarket) return safeSummary;
    return {
      kalshi: {
        count: orders.filter(o => o.platform === 'kalshi').length,
        totalVolume: orders
          .filter(o => o.platform === 'kalshi')
          .reduce((sum, o) => sum + (o.price || 0) * (o.quantity || 0), 0),
      },
      polymarket: {
        count: orders.filter(o => o.platform === 'polymarket').length,
        totalVolume: orders
          .filter(o => o.platform === 'polymarket')
          .reduce((sum, o) => sum + (o.price || 0) * (o.quantity || 0), 0),
      },
    };
  }, [normalizedData.summary, orders]);

  const isActiveStatus = (status: string, remainingQuantity: number): boolean => {
    const normalized = (status || '').toLowerCase();
    if (['resting', 'live', 'partial', 'pending', 'delayed', 'unmatched'].includes(normalized)) {
      return true;
    }
    if (['executed', 'matched', 'canceled', 'cancelled'].includes(normalized)) {
      return false;
    }
    return remainingQuantity > 0;
  };

  const activeOrders = useMemo(() =>
    orders.filter(o => isActiveStatus(o.status, o.remainingQuantity))
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
    [orders]
  );

  const historyOrders = useMemo(() =>
    orders.filter(o => !isActiveStatus(o.status, o.remainingQuantity))
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
    [orders]
  );

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
      <Typography sx={{ color: 'grey.500', fontSize: '0.8rem' }}>Loading orders...</Typography>
    </Box>;
  }

  return (
    <Box>
      {/* Summary bar */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, py: 0.3 }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'white' }}>Orders</Typography>
        <Chip
          icon={<img src="/assets/exchanges/png/100x100/kalshi.png" style={{ width: 12, height: 12, borderRadius: '50%' }} />}
          label={`${summary.kalshi.count ?? 0} · $${(summary.kalshi.totalVolume ?? 0).toFixed(0)}`}
          size="small" sx={{ fontSize: '0.6rem', height: 20 }}
        />
        <Chip
          icon={<img src="/assets/exchanges/png/100x100/polymarket.png" style={{ width: 12, height: 12, borderRadius: '50%' }} />}
          label={`${summary.polymarket.count ?? 0} · $${(summary.polymarket.totalVolume ?? 0).toFixed(0)}`}
          size="small" sx={{ fontSize: '0.6rem', height: 20 }}
        />
      </Stack>

      {/* Two-column layout: Active | History */}
      <Box sx={{ display: 'flex', gap: 0 }}>
        {/* Left: Active orders */}
        <Box sx={{ flex: 1, maxHeight: 400, overflow: 'auto' }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: 'grey.400', px: 0.5, py: 0.2, textTransform: 'uppercase', position: 'sticky', top: 0, backgroundColor: '#1a1a2e', zIndex: 1 }}>
            Active Orders ({activeOrders.length})
          </Typography>
          <OrderTable orders={activeOrders} marketLookup={marketLookup} emptyText="No active orders" />
        </Box>

        {/* Divider */}
        <Box sx={{ width: '2px', backgroundColor: 'rgba(255,255,255,0.15)', flexShrink: 0 }} />

        {/* Right: Completed/Cancelled */}
        <Box sx={{ flex: 1, maxHeight: 400, overflow: 'auto' }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: 'grey.400', px: 0.5, py: 0.2, textTransform: 'uppercase', position: 'sticky', top: 0, backgroundColor: '#1a1a2e', zIndex: 1 }}>
            Closed Orders ({historyOrders.length})
          </Typography>
          <OrderTable orders={historyOrders} marketLookup={marketLookup} emptyText="No closed orders" />
        </Box>
      </Box>
    </Box>
  );
};

/* ------------------------------------------------------------------ */

const OrderTable = ({ orders, marketLookup, emptyText }: {
  orders: NormalizedOrder[];
  marketLookup: Map<string, { name: string; url?: string }>;
  emptyText: string;
}) => {
  const [page, setPage] = useState(0);
  const rowsPerPage = 25;
  const paginated = orders.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  if (orders.length === 0) {
    return <Typography sx={{ color: 'grey.600', fontSize: '0.7rem', textAlign: 'center', py: 2 }}>{emptyText}</Typography>;
  }

  return (
    <>
      <TableContainer>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#1a1a2e' }}>
            <TableRow>
              <TableCell sx={{ ...hSx, width: 20 }}></TableCell>
              <TableCell sx={{ ...hSx, width: 120 }}>Market</TableCell>
              <TableCell sx={{ ...hSx, width: 28 }} align="center">Side</TableCell>
              <TableCell sx={{ ...hSx, width: 30 }} align="center">Act</TableCell>
              <TableCell sx={{ ...hSx, width: 40 }} align="right">Price</TableCell>
              <TableCell sx={{ ...hSx, width: 45 }} align="right">Qty</TableCell>
              <TableCell sx={{ ...hSx, width: 42 }} align="center">Status</TableCell>
              <TableCell sx={{ ...hSx, width: 35 }} align="right">Ago</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.map(order => (
              <OrderRow key={order.id} order={order} marketLookup={marketLookup} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {orders.length > rowsPerPage && (
        <TablePagination
          component="div" count={orders.length} page={page}
          onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[25]}
          sx={{ '& .MuiTablePagination-toolbar': { minHeight: 24 }, '& .MuiTablePagination-displayedRows': { fontSize: '0.6rem' } }}
        />
      )}
    </>
  );
};

const OrderRow = ({ order, marketLookup }: { order: NormalizedOrder; marketLookup: Map<string, { name: string; url?: string }> }) => {
  const platformImg = order.platform === 'kalshi'
    ? '/assets/exchanges/png/100x100/kalshi.png'
    : '/assets/exchanges/png/100x100/polymarket.png';

  // Prefer backend-enriched title/url, fall back to frontend lookup
  const market = marketLookup.get(order.marketId);
  const displayName = order.marketTitle || market?.name || order.marketId;
  const marketUrl = order.marketUrl || market?.url
    || (order.platform === 'kalshi' ? `https://kalshi.com/markets/${order.marketId}` : undefined);

  // Kalshi: show from user's perspective (what position they're closing)
  // "buy YES at $0.85" = user selling NO at $0.15 (1 - 0.85)
  // "sell NO at $0.15" = user selling NO at $0.15 (as-is)
  let displaySide = order.side;
  let displayAction = order.action;
  let displayPrice = order.price;
  if (order.platform === 'kalshi' && order.action === 'buy') {
    displayAction = 'sell';
    displaySide = order.side === 'yes' ? 'no' : 'yes';
    displayPrice = Math.round((1 - order.price) * 100) / 100;
  }

  return (
    <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}>
      <TableCell sx={cSx}>
        <img src={platformImg} alt="" style={{ width: 12, height: 12, borderRadius: '50%' }} />
      </TableCell>

      <TableCell sx={{ ...cSx, overflow: 'hidden' }}>
        <Tooltip title={`${displayName}\n${order.marketId}`} placement="top-start">
          {marketUrl ? (
            <a href={marketUrl} target="_blank" rel="noopener" style={{ textDecoration: 'none', color: 'inherit' }}>
              <Typography sx={{ fontSize: '0.62rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', '&:hover': { color: 'info.main' } }}>
                {displayName}
              </Typography>
            </a>
          ) : (
            <Typography sx={{ fontSize: '0.62rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayName}
            </Typography>
          )}
        </Tooltip>
      </TableCell>

      <TableCell sx={cSx} align="center">
        <Chip label={displaySide.toUpperCase()} size="small"
          color={displaySide === 'yes' ? 'success' : 'error'} variant="outlined"
          sx={{ fontSize: '0.45rem', height: 14, '& .MuiChip-label': { px: 0.3 } }} />
      </TableCell>

      <TableCell sx={cSx} align="center">
        <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, color: displayAction === 'buy' ? 'success.light' : 'error.light' }}>
          {displayAction.toUpperCase()}
        </Typography>
      </TableCell>

      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600 }}>{fmtPrice(displayPrice)}</Typography>
      </TableCell>

      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.62rem' }}>
          {order.filledQuantity}/{order.quantity}
        </Typography>
      </TableCell>

      <TableCell sx={cSx} align="center">
        <Chip label={order.status} size="small"
          color={statusColor[order.status] ?? 'default'} variant="outlined"
          sx={{ fontSize: '0.45rem', height: 14, '& .MuiChip-label': { px: 0.3 } }} />
      </TableCell>

      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.58rem', color: 'grey.500' }}>{timeAgo(order.updatedAt)}</Typography>
      </TableCell>
    </TableRow>
  );
};
